import React,{useEffect,useRef,useState} from 'react';
import {FiCheck,FiChevronDown,FiClock,FiCopy,FiEdit2,FiLayers,FiPlus,FiTrash2,FiX} from 'react-icons/fi';
import {readAssemblyCuts,currentAssemblyCut,MAX_ASSEMBLY_CUTS,assemblyCutDuration,
 duplicateAssemblyCut,renameAssemblyCut,deleteAssemblyCut,switchAssemblyCut} from '../lib/assemblyCuts.js';
import {formatTime} from '../lib/editModel.js';

// Four shared working cuts per project; NOT a timeline of hundreds of versions.
export default function AssemblyCutSwitcher({paper,onChange,onBeforeSwitch=async()=>{},onHistory=()=>{},disabled=false}){
 const cuts=readAssemblyCuts(paper),current=currentAssemblyCut(paper);
 const [open,setOpen]=useState(false),[renaming,setRenaming]=useState(null);
 const [name,setName]=useState(''),[pendingDelete,setPendingDelete]=useState(null);
 const [working,setWorking]=useState(false),[error,setError]=useState('');
 const wrapper=useRef(null),nameField=useRef(null),workingRef=useRef(false);
 const latest=useRef(paper);latest.current=paper;
 useEffect(()=>{
  if(!open)return;
  const outside=e=>{if(!wrapper.current?.contains(e.target)){setOpen(false);setRenaming(null);setPendingDelete(null);setError('');}};
  const escape=e=>{if(e.key==='Escape'){setOpen(false);setRenaming(null);setPendingDelete(null);setError('');}};
  document.addEventListener('pointerdown',outside);
  document.addEventListener('keydown',escape);
  return()=>{document.removeEventListener('pointerdown',outside);document.removeEventListener('keydown',escape);};
 },[open]);
 useEffect(()=>{if(renaming)nameField.current?.focus();},[renaming]);
 const startRename=cut=>{setRenaming(cut.id);setName(cut.name);setPendingDelete(null);setError('');};
 const apply=async(action,{switching=false,keepOpen=false}={})=>{
  if(disabled||workingRef.current)return;
  workingRef.current=true;setWorking(true);setError('');
  const prior=latest.current;
  try{
   if(switching){
    await onBeforeSwitch();
    if(latest.current!==prior)throw Error('The Assembly changed while saving. Try switching again.');
   }
   const next=action(prior);
   if(next!==prior)onChange(next);
   if(!keepOpen){setOpen(false);setRenaming(null);setPendingDelete(null);}
   return next;
  }catch(e){setError(e.message||'Could not change the Assembly cut.');return null;}
  finally{workingRef.current=false;setWorking(false);}
 };
 const rename=event=>{
  event.preventDefault();
  if(!name.trim())return setError('Name this cut before saving.');
  const target=renaming;
  void apply(p=>renameAssemblyCut(p,target,name),{keepOpen:true}).then(next=>{
   if(next){setRenaming(null);setError('');}
  });
 };
 const duplicate=()=>{
  void apply(p=>duplicateAssemblyCut(p),{switching:true,keepOpen:true}).then(next=>{
   if(next){const id=next.assemblyCuts.activeId;setRenaming(id);setName(currentAssemblyCut(next).name);setPendingDelete(null);setOpen(true);}
  });
 };
 const deleteCut=id=>void apply(p=>deleteAssemblyCut(p,id),{switching:true});
 return <div className="assembly-cut-switcher" ref={wrapper}>
  <button type="button" className="assembly-cut-trigger" aria-haspopup="menu" aria-expanded={open}
   aria-label={'Working cut: '+current.name+'. Switch, rename or manage working cuts.'}
   title="Switch between your working cuts"
   disabled={disabled||working} onClick={()=>{setOpen(value=>!value);setError('');setPendingDelete(null);setRenaming(null);}}>
   <FiLayers aria-hidden="true" className="assembly-cut-layers-icon"/>
   <span className="assembly-cut-trigger-name" title={current.name}>{current.name}</span>
   <FiChevronDown aria-hidden="true" className={open?'is-open':''}/>
  </button>
  <button type="button" className={'assembly-cut-quick-add'+(cuts.items.length===1?' is-intro':'')}
   disabled={disabled||working||cuts.items.length>=MAX_ASSEMBLY_CUTS}
   aria-label="Create another working cut"
   title={cuts.items.length>=MAX_ASSEMBLY_CUTS?'Maximum of four working cuts':'Duplicate the current cut and give the new edit its own name'}
   onClick={duplicate}>
   <FiPlus aria-hidden="true"/>
   {cuts.items.length===1&&<span>New cut</span>}
  </button>
  {open&&<div className="assembly-cut-menu" role="group" aria-label="Working Assembly cuts">
   <div className="assembly-cut-menu-title">WORKING CUTS <span>{cuts.items.length} of {MAX_ASSEMBLY_CUTS}</span></div>
   {cuts.items.map(cut=><div className={'assembly-cut-row'+(cut.id===cuts.activeId?' is-current':'')} key={cut.id}>
    {renaming===cut.id?
      <form className="assembly-cut-rename" onSubmit={rename}>
       <input ref={nameField} aria-label="Cut name" maxLength={80} value={name}
        onChange={e=>setName(e.target.value)} disabled={working}
        onKeyDown={e=>{if(e.key==='Escape'){e.stopPropagation();setRenaming(null);}}}/>
       <button type="submit" title="Save name" aria-label="Save cut name" disabled={working||!name.trim()}><FiCheck/></button>
       <button type="button" title="Cancel rename" aria-label="Cancel rename" onClick={()=>setRenaming(null)}><FiX/></button>
      </form>
      :pendingDelete===cut.id?
       <div className="assembly-cut-delete-confirm"><span>Delete “{cut.name}”?</span>
        <button type="button" className="is-danger" disabled={working} onClick={()=>deleteCut(cut.id)}>Delete</button>
        <button type="button" disabled={working} onClick={()=>setPendingDelete(null)}>Cancel</button></div>
      :<>
       <button type="button" className="assembly-cut-pick" disabled={working||disabled}
        aria-current={cut.id===cuts.activeId?'true':undefined}
        onClick={()=>{if(cut.id===cuts.activeId)setOpen(false);else void apply(p=>switchAssemblyCut(p,cut.id),{switching:true});}}>
        <span className="assembly-cut-check">{cut.id===cuts.activeId&&<FiCheck aria-hidden="true"/>}</span>
        <span className="assembly-cut-details"><strong title={cut.name}>{cut.name}</strong>
         <small>{(cut.id===cuts.activeId?paper.blocks:cut.snapshot.blocks).length} passages · {formatTime(assemblyCutDuration(cut.id===cuts.activeId?paper:cut.snapshot))}</small>
        </span>
       </button>
       <button type="button" className="assembly-cut-small-action" aria-label={'Rename '+cut.name} title="Rename cut"
        disabled={working||disabled} onClick={()=>startRename(cut)}><FiEdit2/></button>
       {cuts.items.length>1&&<button type="button" className="assembly-cut-small-action" aria-label={'Delete '+cut.name}
        title="Delete cut" disabled={working||disabled} onClick={()=>{setPendingDelete(cut.id);setRenaming(null);}}><FiTrash2/></button>}
      </>}
   </div>)}
   <div className="assembly-cut-menu-footer">
    <button type="button" className="assembly-cut-duplicate" disabled={working||disabled||cuts.items.length>=MAX_ASSEMBLY_CUTS}
      onClick={duplicate}><FiCopy/> Duplicate current cut</button>
    <button type="button" className="assembly-cut-history-link" onClick={()=>{setOpen(false);onHistory();}}><FiClock/> History</button>
   </div>
   {error&&<p className="assembly-cut-error" role="alert">{error}</p>}
   {working&&<span className="assembly-cut-working" role="status">Saving cut…</span>}
  </div>}
 </div>;
}
